import urls from "global/constants/UrlConstants";
import {
  Workspace,
  WorkspaceApiResponse,
  WorkspaceEnrollment,
} from "types/workspace";
import { getCallParams, makeCall } from "utils/service";

export const fetchWorkspaces = async (
  includeArchived: boolean = false,
): Promise<Workspace[]> => {
  const url = `${urls.WORKSPACE_FETCH}?includeArchived=${includeArchived}`;
  try {
    const callParams = await getCallParams("GET");
    const response: Workspace[] = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The caller's effective permissions in the active workspace, for UI gating.
 * {scoped: is a workspace active, admin: bypass, permissions: the caller's permission keys,
 * delegatedAdmin: Workspace Admin of at least one workspace}.
 */
export const fetchMyWorkspacePermissions = async (): Promise<{
  scoped: boolean;
  admin: boolean;
  permissions: string[];
  delegatedAdmin: boolean;
}> => {
  // Errors propagate rather than resolving with a default here: the caller (WorkspaceSwitcher)
  // needs to see the failure to decide fail-open vs fail-closed based on whether a workspace is
  // currently active — swallowing it here would make that decision unreachable.
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.WORKSPACE_MY_PERMISSIONS, callParams);
    return {
      scoped: Boolean(response?.scoped),
      admin: Boolean(response?.admin),
      permissions: Array.isArray(response?.permissions) ? response.permissions : [],
      delegatedAdmin: Boolean(response?.delegatedAdmin),
    };
  } catch (error: any) {
    throw error;
  }
};

/** The current user's own workspaces (membership-based) — for the general switcher. */
export const fetchMyWorkspaces = async (): Promise<Workspace[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: Workspace[] = await makeCall(urls.WORKSPACE_MINE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Creates a top-level workspace, or a sub-workspace when `parentId` is given. */
export const createWorkspace = async (
  name: string,
  parentId?: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST", parentId ? { name, parentId } : { name });
    const response: WorkspaceApiResponse = await makeCall(
      urls.WORKSPACE_ADD,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const renameWorkspace = async (
  id: string,
  name: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("PUT", { name });
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_UPDATE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const archiveWorkspace = async (
  id: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST");
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_ARCHIVE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const unarchiveWorkspace = async (
  id: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST");
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_UNARCHIVE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Gives a workspace a new parent (account admins only); `parentId` null moves it to the top level.
 * Envelope quotas are per workspace, so a move never changes any quota.
 */
export const moveWorkspace = async (
  id: string,
  parentId: string | null,
): Promise<{ moved: boolean }> => {
  try {
    const callParams = await getCallParams("PUT", { parentId });
    const response: { moved: boolean } = await makeCall(
      `${urls.WORKSPACE_MOVE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Which workspaces the users whose email contains `text` belong to, and their role in each. */
export const searchEnrollment = async (
  text: string,
): Promise<WorkspaceEnrollment[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: WorkspaceEnrollment[] = await makeCall(
      `${urls.WORKSPACE_ENROLLMENT_SEARCH}?q=${encodeURIComponent(text)}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
